import React from 'react'

import 'browser/components/atomic-elements/organisms/message-template-editor/_message-template-editor.scss'
import { FormTable } from '../../atoms/form-table/form-table'
import { SelectField } from '../../molecules/fields/select-field'
import { Label } from '../../atoms/label/label'
import { FormGroup } from '../../atoms/form-group/form-group'
import { HelpBlock } from '../../atoms/help-block/help-block'
import _ from 'lodash'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import apis from 'browser/app/models/apis'
import {
  TypeaheadTextarea,
  ITypeaheadItem,
} from '../../atoms/typeahead-textarea/typeahead-textarea'

export enum SaveMode {
  Template = 'template',
  Resolved = 'resolved',
}

export interface IMessageTemplateVariable extends ITypeaheadItem {
  alias: string
  fullExpression: string
  sampleValue?: string
}

/**
 * @uiComponent
 */
export interface IMessageTemplateEditorProps {
  label: string
  labelProps?: any
  variables: IMessageTemplateVariable[]
  messageTemplates?: string[]
  context?: any
  saveMode?: SaveMode
  value?: string
  showSampleMessage?: boolean
  showSavePreview?: boolean
  minRows?: number
  onChange?: (value: string) => void
}

interface IMessageTemplateEditorState {
  editorValue: string

  typeaheadIsOpen: boolean
  typeaheadQuery: string
  typeaheadStart: number
  typeaheadSuggestionIndex: number

  typeaheadPositionX?: number
  typeaheadPositionY?: number
}

const NAMED_VAR_TOKEN = /\${\s*([\s\S]+?)\s*}/g
const TRIGGER_TOKEN = '/'
const START_TOKEN = '${'
const END_TOKEN = '}'
const HELP_TEXT = `Type ${TRIGGER_TOKEN} to add variables`

export class MessageTemplateEditor extends React.Component<
  IMessageTemplateEditorProps,
  IMessageTemplateEditorState
> {
  public static defaultProps: Partial<IMessageTemplateEditorProps> = {
    variables: [],
    messageTemplates: [],
    saveMode: SaveMode.Template,
    label: 'Message Template',
    showSampleMessage: true,
    showSavePreview: true,
  }

  constructor(props: IMessageTemplateEditorProps) {
    super(props)
    const { value, variables } = props
    this.state = {
      editorValue: value ? rewriteWithAliases(value, variables) : undefined, // constructor only - add back aliases from saved value for better readability

      typeaheadIsOpen: false,
      typeaheadQuery: '',
      typeaheadStart: -1,
      typeaheadSuggestionIndex: 0,

      typeaheadPositionX: undefined,
      typeaheadPositionY: undefined,
    }
  }

  public render() {
    const { label, labelProps, variables, minRows, onChange } = this.props
    const { editorValue } = this.state
    const hasVariables = variables && variables.length > 0

    const typeaheadItems = variables.map((v) => ({
      ...v,
      insertText: `${START_TOKEN}${v.alias}${END_TOKEN}`,
    }))

    return (
      <>
        <FormTable>
          {this.renderTemplatePicker()}
          <TypeaheadTextarea
            label={label}
            labelProps={labelProps}
            items={typeaheadItems}
            value={editorValue}
            onChange={this.handleChange}
            triggerToken={TRIGGER_TOKEN}
            itemRenderer={this.renderTypeaheadItem}
          >
            {hasVariables && <HelpBlock className="c-mte-help" helpText={HELP_TEXT} />}
          </TypeaheadTextarea>
          {this.renderSampleMessage()}
        </FormTable>
        {this.renderSavePreview()}
      </>
    )
  }

  private renderTypeaheadItem(item: IMessageTemplateVariable): React.ReactNode {
    return (
      <>
        <div className='fw6'>
          {item.label}
          <span style={{ opacity: 0.6 }}>• {`${START_TOKEN}${item.alias}${END_TOKEN}`}</span>
        </div>
        <div style={{ fontSize: 12, opacity: 0.8 }}>{item.description}</div>
        {item.sampleValue != null && (
          <div style={{ fontSize: 12 }}>
            <em>e.g.</em> {String(item.sampleValue)}
          </div>
        )}
      </>
    )
  }

  private renderTemplatePicker() {
    const { messageTemplates } = this.props
    if (!messageTemplates || messageTemplates.length === 0) return null
    return (
      <SelectField
        label="Templates"
        placeholder="Select a template to prefill"
        options={messageTemplates.map((_, i) => ({
          label: _,
          value: i,
        }))}
        onChange={this.handleTemplatePick}
      />
    )
  }

  private renderSampleMessage() {
    const { showSampleMessage, context, labelProps, saveMode } = this.props
    const { editorValue } = this.state
    if (!showSampleMessage || !editorValue) return null
    return (
      <FormGroup isHorizontalLayout={true}>
        <Label
          isHorizontalLayout={true}
          text={saveMode === SaveMode.Resolved ? 'Message Preview' : 'Sample Message'}
          {...labelProps}
        />
        <div className="c-mte-sample">
          <div>{resolveTemplate(editorValue, this.aliasMap(), this.sampleMap(), context)}</div>
        </div>
      </FormGroup>
    )
  }

  private renderSavePreview() {
    const { showSavePreview, value } = this.props
    if (!value || !showSavePreview) return null
    return (
      <details style={{ marginLeft: 'auto' }}>
        <summary style={{ cursor: 'pointer' }}>What will be saved?</summary>
        <pre className="c-mte-preview">{value}</pre>
      </details>
    )
  }

  /* ---------- maps ---------- */

  private aliasMap(): Record<string, string> {
    const { variables } = this.props
    const map: Record<string, string> = {}
    for (const v of variables) map[v.alias] = v.fullExpression
    return map
  }

  private sampleMap(): Record<string, string | undefined> {
    const { variables } = this.props
    const map: Record<string, string | undefined> = {}
    for (const v of variables) map[v.alias] = v.sampleValue
    return map
  }

  /* ---------- persist/preview ---------- */

  private computePersisted(): {
    toPersist: string
    mode: SaveMode
    used: string[]
  } {
    const { context, saveMode = SaveMode.Template } = this.props
    const { editorValue } = this.state
    const aliasMap = this.aliasMap()
    const used = extractTokens(editorValue)

    const toPersist =
      saveMode === SaveMode.Resolved
        ? resolveTemplate(editorValue, aliasMap, this.sampleMap(), context)
        : aliasToFullExpression(editorValue, aliasMap)

    return { toPersist, mode: saveMode, used }
  }

  /* ---------- events ---------- */

  private handleChange = (nextValue: string) => {
    const { onChange } = this.props
    this.setState({ editorValue: nextValue }, () => {
      onChange?.(this.computePersisted().toPersist)
    })
  }

  private handleTemplatePick = (idx) => {
    if (idx >= 0) {
      const tpl = this.props.messageTemplates[idx] ?? ''
      this.handleChange(tpl)
    }
  }
}

/*---------- utils ---------- */

function extractTokens(tpl: string): string[] {
  const out = new Set<string>()
  let m: RegExpExecArray | null
  while ((m = NAMED_VAR_TOKEN.exec(tpl))) out.add(String(m[1]).trim())
  return [...out]
}

function aliasToFullExpression(tpl: string, aliasMap: Record<string, string>): string {
  return tpl.replace(NAMED_VAR_TOKEN, (full, inner) => {
    const key = String(inner).trim()
    const mapped = aliasMap[key]
    return mapped ? `${mapped}` : full
  })
}

/*
 * Creates a regex that matches the expression with flexible whitespace and either single or double quotes around string literals
 */
function makeLooseExprRegex(fullExpression: string): RegExp {
  const trimmedExpression = fullExpression.trim()

  // Escape + make whitespace flexible
  const escapeFixed = (s: string) => _.escapeRegExp(s).replace(/\s+/g, '\\s*')

  const quotedStringsPattern = /(['"])([^'"]*?)\1/g

  let resultPattern = ''
  let last = 0
  let m: RegExpExecArray | null

  // build regex by alternating between fixed parts and quoted literals
  while ((m = quotedStringsPattern.exec(trimmedExpression))) {
    const before = trimmedExpression.slice(last, m.index)
    const literal = m[2] // inside quotes

    resultPattern += escapeFixed(before)
    resultPattern += `(["'])${literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\1`
    last = m.index + m[0].length
  }

  resultPattern += escapeFixed(trimmedExpression.slice(last))
  return new RegExp(resultPattern, 'g')
}

/**
 * Adds aliases to the editor where possible for a better user experience
 */
function rewriteWithAliases(message: string, variables: IMessageTemplateVariable[]): string {
  let aliasedEditorValue = message
  const reverseMap = reverseAliasMap(variables)
  // Replace longer snippets first so small ones don't consume parts of big ones
  const entries = Object.entries(reverseMap).sort((a, b) => b[0].length - a[0].length)

  for (const [fullExpr, alias] of entries) {
    const re = makeLooseExprRegex(fullExpr)
    aliasedEditorValue = aliasedEditorValue.replace(re, `${START_TOKEN}${alias}${END_TOKEN}`)
  }
  return aliasedEditorValue
}

function reverseAliasMap(variables: IMessageTemplateVariable[]): Record<string, string> {
  const map: Record<string, string> = {}
  for (const v of variables) {
    map[v.fullExpression] = v.alias
  }
  return map
}

/**
 * Resolves tokens using context or sample values; otherwise keep/empty
 */
function resolveTemplate(
  tpl: string,
  aliasMap: Record<string, string>,
  sampleMap: Record<string, string | undefined>,
  context?: any
): string {
  if (context) {
    const settings = apis.getSettings()
    const replaceTokens = tpl.replace(NAMED_VAR_TOKEN, (full, inner) => {
      const friendly = String(inner).trim()
      return aliasMap[friendly] ?? friendly
    })
    return _.template(replaceTokens)({ ...context, settings, ...CustomFormulas })
  }
  return _.template(tpl)(sampleMap)
}
